import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { FATE_DIE_URL } from '../../../../Settings';
import Die from './Die';

import './FateDieOverlay.css';


// Sentinel event id for the cosmetic ack relay (DIE_ROLL_ACK). When the
// roller's frontend acks the dice click, the backend re-broadcasts this
// event to the game group so observers know to start their tumble.
const DIE_ROLL_ACK_EVENT_ID = 36;

// How long to leave the settled dice on screen after the tumble lands,
// before calling eventComplete and dismissing the overlay.
const SETTLE_HOLD_MS = 600;

// Watchdog — if the tumble never starts (e.g., ack relay never arrives),
// give up after this many ms and let the event queue advance anyway.
const WATCHDOG_MS = 10000;

// Manual §8.0 / §12: First Player rolls every die at the table. The
// die is THEIRS to tap — but to spare the human First Player from
// hand-tapping a die for every other seat's action, the roller's own
// client auto-rolls after this countdown if they don't tap first.
// Mirrors AutoCloseButton in Default.js — Wait button extends
// indefinitely, Roll(Ns) button fires immediately, 0s auto-fires.
const ROLLER_AUTO_ROLL_COUNTDOWN_S = 5;

// Manual: Fate Die has two blank faces (sides 1, 2 = zero events).
// Blank faces render as plain die-color panels — Die.js skips the <img>
// when the entry is null. The white Side1/Side2 PNGs are intentionally
// not referenced; the physical in-box dice are solid black.
const FATE_DIE_FACES = [
    null,
    null,
    `${FATE_DIE_URL}Side3_Events1.png`,
    `${FATE_DIE_URL}Side4_Events1.png`,
    `${FATE_DIE_URL}Side5_Events1.png`,
    `${FATE_DIE_URL}Side6_Events2.png`,
];

const FATE_FACE_LABELS = [
    'Fate Die showing 1, zero events',
    'Fate Die showing 2, zero events',
    'Fate Die showing 3, one event',
    'Fate Die showing 4, one event',
    'Fate Die showing 5, one event',
    'Fate Die showing 6, two events',
];


function detectReducedMotion() {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}


function clampFaceValue(raw) {
    if (typeof raw !== 'number') return null;
    if (!Number.isFinite(raw)) return null;
    const v = Math.round(raw);
    if (v < 1 || v > 6) return null;
    return v;
}


function readFateResult(data) {
    if (!data) return 1;
    const face = clampFaceValue(data.face);
    if (face !== null) return face;
    const rolled = clampFaceValue(data.rolled_value);
    if (rolled !== null) return rolled;
    return 1;
}


function resolveRollerSeat(rollerId, gameData) {
    if (rollerId === null || rollerId === undefined) return null;
    if (!gameData || !Array.isArray(gameData.gameplayer_set)) return null;
    const roller = gameData.gameplayer_set.find((p) => p.id === rollerId);
    if (roller && roller.seat !== undefined) return roller.seat;
    return null;
}


// Match the convention from ArtificialPlayer.js (lines 189, 253):
// artificial_player > 2 means the seat is a fully-automated AI roller.
// Values 0-2 are the various human / debug bands and must not auto-tap.
function rollerIsArtificial(rollerId, gameData) {
    if (rollerId === null || rollerId === undefined) return false;
    if (!gameData || !Array.isArray(gameData.gameplayer_set)) return false;
    const roller = gameData.gameplayer_set.find((p) => p.id === rollerId);
    if (!roller) return false;
    const ai = roller.artificial_player;
    return typeof ai === 'number' && ai > 2;
}


// Fallback path: if gameData.gameplayer_set doesn't carry
// artificial_player (or has it in a shape rollerIsArtificial can't
// parse), reach the per-seat GamePlayerController via app.game and
// read its gamePlayerData.artificial_player directly. Each
// GamePlayerController owns its own gamePlayerData and updates on
// every game-player-N-event.
function rollerIsArtificialFallback(rollerId, app) {
    try {
        if (!app || !app.game) return false;
        const controllers = app.game.gamePlayerControllers;
        if (!Array.isArray(controllers)) return false;
        for (const c of controllers) {
            if (!c || !c.gamePlayerData) continue;
            if (c.gamePlayerData.id !== rollerId) continue;
            const ai = c.gamePlayerData.artificial_player;
            return typeof ai === 'number' && ai > 2;
        }
        return false;
    } catch (err) {  // eslint-disable-line no-unused-vars
        return false;
    }
}


// Mirror ArtificialPlayer.js line 251: only the group leader client
// runs the AIs, so only the group leader should fire ack on their
// behalf. Otherwise every observer would fire and the backend would
// re-broadcast N copies of the relay.
function isGroupLeader(app) {
    try {
        const gh = app && app.game && app.game.groupHandler;
        if (gh && typeof gh.isGroupLeader === 'function') {
            return !!gh.isGroupLeader();
        }
    } catch (err) {  // eslint-disable-line no-unused-vars
        return false;
    }
    return false;
}


// Group payload is async. memberRecord.user gets set when the matched
// member arrives; _ownerUserId tracks the lobby owner. Both must be
// present before isGroupLeader can return a meaningful answer. Until
// then, the piece-1 useEffect treats a false from isGroupLeader as
// "not yet" and polls.
function groupHandlerIsReady(app) {
    try {
        const gh = app && app.game && app.game.groupHandler;
        if (!gh) return false;
        if (gh._ownerUserId === null || gh._ownerUserId === undefined) {
            return false;
        }
        if (!gh.memberRecord || !gh.memberRecord.user) return false;
        return true;
    } catch (err) {  // eslint-disable-line no-unused-vars
        return false;
    }
}


// Solo-vs-AI safety net: if there is exactly one human user in the
// game, that user's client is unambiguously the one that should fire
// the ack on the AI's behalf — there are no other humans who could
// race with them.
function countHumanSeats(gameData) {
    if (!gameData || !Array.isArray(gameData.gameplayer_set)) return 0;
    let n = 0;
    for (const p of gameData.gameplayer_set) {
        if (!p) continue;
        const ai = p.artificial_player;
        const isAI = typeof ai === 'number' && ai > 2;
        if (!isAI) n++;
    }
    return n;
}


// Diagnostic logger. Opt-in: set localStorage.HSH_DICE_DEBUG = '1' in
// the browser console and reload to see one bracketed line per dice
// mount showing every guard's resolved value. Wrapped in try/catch so
// sandboxed contexts that throw on storage access stay silent.
function debugLog(tag, payload) {
    try {
        if (typeof window === 'undefined') return;
        if (window.localStorage
                && window.localStorage.getItem('HSH_DICE_DEBUG') === '1') {
            console.log('[' + tag + ']', payload);
        }
    } catch (err) {  // eslint-disable-line no-unused-vars
        // No-op. Storage access can throw in some sandboxed contexts.
    }
}


function detectAutoMode(app) {
    try {
        if (app && app.game
                && typeof app.game.automaticPlayerIsEngaged === 'function') {
            return !!app.game.automaticPlayerIsEngaged();
        }
    } catch (err) {  // eslint-disable-line no-unused-vars
        return false;
    }
    return false;
}


function pickDieResult(globalResult, dieIndex, dieCount, payloadResults) {
    if (Array.isArray(payloadResults) && payloadResults.length > dieIndex) {
        const v = clampFaceValue(payloadResults[dieIndex]);
        if (v !== null) return v;
    }
    if (dieCount > 1 && dieIndex < dieCount - 1) {
        return (((globalResult + dieIndex) % 6) + 1);
    }
    return globalResult;
}


export default function FateDieOverlay({
        receivedMessage, app, gameData, gamePlayerData, eventComplete,
        // eslint-disable-next-line no-unused-vars
        onClockOut }) {
    const messageData = (receivedMessage && receivedMessage.package
            && receivedMessage.package.data) || {};
    const rollerId = (messageData.rolling_player !== undefined
            && messageData.rolling_player !== null)
        ? messageData.rolling_player
        : (gameData && gameData.player_one !== undefined
                ? gameData.player_one : null);
    const seatedId = (gamePlayerData && gamePlayerData.id !== undefined)
        ? gamePlayerData.id : null;
    const isRoller = (rollerId !== null && rollerId === seatedId);
    const isAuto = detectAutoMode(app);

    const rawDieCount = Number(messageData.dice_count);
    const dieCount = (Number.isFinite(rawDieCount) && rawDieCount > 0)
        ? Math.min(Math.max(1, Math.round(rawDieCount)), 2)
        : 1;
    const result = readFateResult(messageData);
    const payloadResults = Array.isArray(messageData.results)
        ? messageData.results : null;
    const rollerSeat = resolveRollerSeat(rollerId, gameData);

    const [phase, setPhase] = useState('idle');
    // Render bumper for the auto-mid-overlay path: when the rAF poll
    // observes a false→true transition on automaticPlayerIsEngaged,
    // incrementing this state triggers a re-render so the function body
    // re-runs `const isAuto = detectAutoMode(app)` and the bottom
    // `if (isAuto) return null` short-circuit fires. setPhase((p) => p)
    // would bail out per React's Object.is bail-out, so it isn't enough.
    const [, bumpAutoRender] = useState(0);
    const [countdown, setCountdown] = useState(ROLLER_AUTO_ROLL_COUNTDOWN_S);
    const countdownPausedRef = useRef(false);
    const phaseRef = useRef('idle');
    const ackSentRef = useRef(false);
    const completedRef = useRef(false);
    const settleHoldTimerRef = useRef(null);
    const eventCompleteRef = useRef(eventComplete);

    useEffect(() => { eventCompleteRef.current = eventComplete; },
        [eventComplete]);

    const finish = useCallback(() => {
        if (completedRef.current) return;
        completedRef.current = true;
        const fn = eventCompleteRef.current;
        if (fn) fn();
    }, []);

    // Clear the settle-hold timer on unmount so a pre-empt (or any other
    // teardown) during the 600ms hold can't fire finish() / dispatch
    // 'die-settled' after the component is gone (I6).
    useEffect(() => {
        return () => {
            if (settleHoldTimerRef.current) {
                clearTimeout(settleHoldTimerRef.current);
                settleHoldTimerRef.current = null;
            }
        };
    }, []);

    const sendAck = useCallback(() => {
        if (ackSentRef.current) return;
        ackSentRef.current = true;
        const gc = app && app.gameController;
        if (gc && typeof gc.sendDieRollAck === 'function') {
            debugLog('FateDieOverlay sendAck', { path: 'gameController' });
            gc.sendDieRollAck();
            return;
        }
        // Fallback: production never instantiates GameController, so
        // app.gameController is null. (Verified live 2026-05-22; class
        // is dead per CLAUDE.md "GameController API rot".) Send the
        // same wire message directly through the authenticator —
        // GameController.sendDieRollAck is a thin wrapper around this
        // call anyway, so there is no behavior difference.
        const auth = app && app.state && app.state.authenticator;
        if (auth && typeof auth.sendMessage === 'function') {
            debugLog('FateDieOverlay sendAck', { path: 'authenticator' });
            auth.sendMessage('DieRollAck', {
                type: 'DieRollAck',
                data: { type: 'DieRollAck' },
            });
            return;
        }
        // Neither path is reachable — the ack is silently dropped and no
        // observer will ever receive the relay. Surface it under the
        // dice-debug flag so a stuck-overlay repro can confirm/rule this out.
        debugLog('FateDieOverlay sendAck', { path: 'NONE — ack dropped' });
    }, [app]);

    const startTumble = useCallback(() => {
        if (phaseRef.current !== 'idle') return;
        phaseRef.current = 'tumbling';
        setPhase('tumbling');
    }, []);

    // Auto-mode skip: the seated player has opted out of watching plays
    // unfold. Don't render the overlay at all. Roller still sends the
    // cosmetic ack so any non-auto observer clients get the relay and
    // can move on. eventComplete fires immediately to advance this
    // client's local event queue past the die event.
    useEffect(() => {
        if (!isAuto) return;
        if (isRoller) sendAck();
        finish();
    }, [isAuto, isRoller, sendAck, finish]);

    // Auto-toggled mid-overlay: the mount-time effect above only fires
    // if isAuto was already true at mount. If the seated player flips
    // Auto on WHILE the overlay is on screen, isAuto stays stale (the
    // component doesn't re-render on toggle), so the overlay sticks
    // until WATCHDOG_MS. Poll automaticPlayerIsEngaged() each animation
    // frame; on the false→true transition, mirror the mount-time skip:
    // roller sends the cosmetic ack, finish() advances the queue, and
    // bumpAutoRender forces a re-render so detectAutoMode(app) is
    // re-evaluated and the `if (isAuto) return null` short-circuit
    // dismisses the portal. rAF (not setInterval) coalesces with the
    // paint cycle and pauses on hidden tabs — matches the observable
    // semantic. Cleanup is pure cancelAnimationFrame; no side effects
    // (CLAUDE.md StrictMode trap).
    useEffect(() => {
        if (isAuto) return undefined;
        let handle = 0;
        let cancelled = false;
        function tick() {
            if (cancelled) return;
            if (detectAutoMode(app)) {
                if (isRoller) sendAck();
                finish();
                bumpAutoRender((n) => n + 1);
                return;
            }
            handle = window.requestAnimationFrame(tick);
        }
        handle = window.requestAnimationFrame(tick);
        return () => {
            cancelled = true;
            if (handle) window.cancelAnimationFrame(handle);
        };
    }, [isAuto, isRoller, app, sendAck, finish]);

    // Piece 1 — AI roller auto-tap. When the rolling seat is an AI and
    // this client is empowered to act on its behalf, fire the cosmetic
    // ack ourselves after a short delay so observers don't stare at
    // idle dice waiting for a click that will never happen. The
    // existing ack-relay listener below picks the ack up via the
    // event-event bus and starts the tumble for everyone, exactly as
    // if a human had tapped.
    //
    // "Empowered" = group leader (multi-user games) OR the only human
    // user (solo-vs-AI, where the group handler may not have settled
    // its memberRecord.user / _ownerUserId at mount time and
    // isGroupLeader returns a stale false). In multi-human games the
    // non-leaders fall through to the relay listener path.
    //
    // AI detection runs both via gameplayer_set (primary) and via the
    // per-seat GamePlayerController (fallback) so a partial gameData
    // payload doesn't strand the overlay.
    useEffect(() => {
        const roller = (gameData && Array.isArray(gameData.gameplayer_set))
            ? gameData.gameplayer_set.find((p) => p.id === rollerId) : null;
        const aiPrimary = rollerIsArtificial(rollerId, gameData);
        const aiFallback = rollerIsArtificialFallback(rollerId, app);
        const rollerIsAI = aiPrimary || aiFallback;
        const leaderNow = isGroupLeader(app);
        const ghReady = groupHandlerIsReady(app);
        const humans = countHumanSeats(gameData);
        const solo = (humans === 1);
        const willFire = !isAuto && !isRoller && rollerIsAI
            && (leaderNow || solo);
        debugLog('FateDieOverlay piece1 mount', {
            rollerId: rollerId,
            seatedId: seatedId,
            isAuto: isAuto,
            isRoller: isRoller,
            rollerData: roller,
            rollerArtificialPlayer: roller && roller.artificial_player,
            rollerArtificialPlayerType: roller
                && typeof roller.artificial_player,
            rollerIsAI_primary: aiPrimary,
            rollerIsAI_fallback: aiFallback,
            isLeader: leaderNow,
            groupHandlerReady: ghReady,
            humanCount: humans,
            willFire: willFire,
        });
        if (isAuto) return undefined;
        if (isRoller) return undefined;
        if (!rollerIsAI) return undefined;
        if (leaderNow || solo) {
            // This client is already the empowered driver (group leader)
            // or the sole human. Fire the cosmetic ack after a short beat.
            const t = setTimeout(() => {
                debugLog('FateDieOverlay piece1 firing sendAck',
                    { rollerId, leader: leaderNow, solo });
                sendAck();
            }, 200);
            return () => clearTimeout(t);
        }
        // Leadership has not resolved for this client yet. Do NOT
        // poll-and-exhaust: the previous bounded setInterval gave up after
        // 1500ms, so when the Members broadcast that establishes leadership
        // landed later — the reconnect / hard-refresh race, where leadership
        // (Members) and game-state (Resume) arrive as independent, unordered
        // async pushes — the AI roller's cosmetic ack never fired and the
        // dice hung until the 10s watchdog (WATCHDOG_MS). Latch on the same
        // group bus the AI driver rides instead: GameGroupHandler
        // .receiveGroupMembers dispatches 'group-update-event' every time
        // leadership can newly resolve. Re-check on each one and fire the ack
        // the moment this client becomes the empowered driver — however late.
        // sendAck is idempotent (ackSentRef), so a redundant fire is a no-op,
        // and a client that never becomes leader never acks (the true leader
        // does). Mirrors the state-driven latch that replaced the analogous
        // exhausting path in ArtificialPlayer (reconnectDriveArmed);
        // HSH_STYLE §Data Fetching bans setInterval polling for exactly this
        // stranding failure mode.
        function onGroupUpdate() {
            if (groupHandlerIsReady(app) && isGroupLeader(app)) {
                debugLog('FateDieOverlay piece1 latch won', { rollerId });
                sendAck();
            }
        }
        window.addEventListener('group-update-event', onGroupUpdate);
        // Close the gap where leadership resolved between this effect's
        // render (when leaderNow was sampled) and the listener attaching.
        onGroupUpdate();
        return () => window.removeEventListener(
            'group-update-event', onGroupUpdate);
    }, [isAuto, isRoller, rollerId, seatedId, gameData, app, sendAck]);

    const handleRollerClick = useCallback(() => {
        if (!isRoller || phaseRef.current !== 'idle') return;
        sendAck();
        startTumble();
    }, [isRoller, sendAck, startTumble]);

    // Wait button: kill the auto-roll countdown so the die never fires on
    // its own. Intentionally no resume — once the First Player hits Wait,
    // the die is theirs to tap when they're ready (the displayed value
    // just freezes). Mirrors AutoCloseButton.stopTimer in Default.js.
    const handleWaitClick = () => {
        countdownPausedRef.current = true;
    };

    // Designated-roller auto-roll countdown. When THIS client is the
    // First Player (the Designated Dice Roller), tick down once a second
    // and auto-roll at 0 instead of demanding a hand-tap for every other
    // seat's Fate die ("roll over and over with nothing happening"
    // trap). Mirrors AutoCloseButton (Default.js): the Wait button
    // (countdownPausedRef) extends indefinitely; the Roll button and a
    // die tap both fire immediately via handleRollerClick, which is
    // idempotent through the phaseRef guard. Only one client is ever
    // isRoller, so there's no multi-fire risk. AI rollers are covered by
    // the leader/solo auto-tap (piece 1).
    useEffect(() => {
        if (isAuto) return undefined;
        if (!isRoller) return undefined;
        if (phase !== 'idle') return undefined;
        let timer = null;
        let remaining = ROLLER_AUTO_ROLL_COUNTDOWN_S;
        function tick() {
            if (countdownPausedRef.current) return;
            remaining -= 1;
            setCountdown(remaining);
            if (remaining <= 0) {
                handleRollerClick();
                return;
            }
            timer = setTimeout(tick, 1000);
        }
        timer = setTimeout(tick, 1000);
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [isAuto, isRoller, phase, handleRollerClick]);

    const handleDieSettled = () => {
        if (phaseRef.current === 'settled') return;
        debugLog('FateDieOverlay handleDieSettled', { isRoller });
        phaseRef.current = 'settled';
        setPhase('settled');
        // Keep __dieRolling=true and defer the 'die-settled' broadcast
        // until the overlay actually dismisses. The result face is still
        // on screen during SETTLE_HOLD_MS and a follow-up die may be
        // queued behind us — consumers shouldn't reveal between rolls.
        // Held in a ref so a pre-empt (next-die-cancels-current) or any
        // other mid-hold unmount clears it — otherwise the timer would
        // fire after unmount and call finish() → eventComplete, clobbering
        // the overlay that pre-empted us (I6).
        settleHoldTimerRef.current = setTimeout(() => {
            settleHoldTimerRef.current = null;
            window.__dieRolling = false;
            window.dispatchEvent(new CustomEvent('die-settled'));
            finish();
        }, SETTLE_HOLD_MS);
    };

    // Mark the global die-rolling state and broadcast a 'die-roll-started'
    // event so consumers can cancel any pending reveal when a new die in
    // a sequence shows up. Cleared on dismiss (above) and on unmount.
    useEffect(() => {
        if (isAuto) return undefined;
        window.__dieRolling = true;
        window.dispatchEvent(new CustomEvent('die-roll-started'));
        return () => { window.__dieRolling = false; };
    }, [isAuto]);

    // Observer path: listen for the DIE_ROLL_ACK relay and start the
    // tumble. The roller's own client receives the echo here too, but
    // the phase guard inside startTumble keeps it idempotent.
    useEffect(() => {
        if (isAuto) return undefined;
        function handleEvent(e) {
            const detail = e && e.detail;
            const eventId = detail && detail.package
                && detail.package.data && detail.package.data.event;
            if (eventId !== DIE_ROLL_ACK_EVENT_ID) return;
            debugLog('FateDieOverlay observer relay received', { eventId });
            startTumble();
        }
        window.addEventListener('event-event', handleEvent);
        return () => window.removeEventListener('event-event', handleEvent);
    }, [isAuto, startTumble]);

    // Watchdog — if the ack never arrives, dismiss anyway so the UI
    // can't get stuck on a Fate moment. Engine has long since moved on.
    useEffect(() => {
        if (isAuto) return undefined;
        const timer = setTimeout(() => {
            if (phaseRef.current === 'idle') finish();
        }, WATCHDOG_MS);
        return () => clearTimeout(timer);
    }, [isAuto, finish]);

    // Piece 3 — EOG safety net. drainEvents normally unmounts us by
    // setting eventComponent: null on the EventRouter, which is the
    // primary teardown path. This listener is the belt-and-suspenders
    // backup: if any code path skips drainEvents but still fires the
    // game-ended window event from GameController.drainQueue, we finish
    // ourselves rather than hang until WATCHDOG_MS.
    useEffect(() => {
        function handleGameEnded() { finish(); }
        window.addEventListener('game-ended', handleGameEnded);
        return () => window.removeEventListener('game-ended', handleGameEnded);
    }, [finish]);

    if (isAuto) return null;

    // Auto-mode treats dice as reduced-motion: snap to the face instantly,
    // skip the tumble. Keeps headless / auto playthroughs fast.
    const reduced = isAuto || detectReducedMotion();
    const rollerLabel = (rollerSeat !== null && rollerSeat !== undefined)
        ? `Player ${rollerSeat + 1}`
        : 'First Player';
    let headline;
    if (phase === 'idle') {
        headline = isRoller
            ? 'Tap the die to roll'
            : `Waiting for ${rollerLabel} to roll...`;
    } else if (phase === 'tumbling') {
        headline = isRoller ? 'Rolling!' : `${rollerLabel} rolling...`;
    } else {
        headline = isRoller ? 'You rolled.' : `${rollerLabel} rolled.`;
    }

    const diceWrapperClasses = [
        'fate-die-overlay__dice',
        `fate-die-overlay__dice--count-${dieCount}`,
    ];
    if (phase === 'idle' && isRoller) {
        diceWrapperClasses.push('fate-die-overlay__dice--clickable');
    }

    const overlay = (
        <div
            className={'fate-die-overlay'}
            role={'status'}
            aria-live={'polite'}
            aria-label={'Fate Die roll'}
            data-testid={'fate-die-overlay'}
            data-phase={phase}
        >
            <div className={'fate-die-overlay__scrim'}/>
            <div className={'fate-die-overlay__stage'}>
                <div className={'fate-die-overlay__roller'}>{headline}</div>
                <div
                    className={diceWrapperClasses.join(' ')}
                    data-die-count={dieCount}
                    data-testid={'fate-die-overlay-dice'}
                    onClick={
                        phase === 'idle' && isRoller
                            ? handleRollerClick : undefined
                    }
                    role={phase === 'idle' && isRoller ? 'button' : undefined}
                    tabIndex={phase === 'idle' && isRoller ? 0 : undefined}
                    onKeyDown={
                        phase === 'idle' && isRoller
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleRollerClick();
                                }
                            }
                            : undefined
                    }
                >
                    {Array.from({ length: dieCount }).map((_, i) => {
                        const isLast = (i === dieCount - 1);
                        const dieResult = pickDieResult(
                            result, i, dieCount, payloadResults);
                        // Key intentionally stable from tumbling onward so
                        // the Die isn't remounted on settle (which would
                        // restart the animation from rest = blank face).
                        const phaseKey =
                            phase === 'idle' ? 'idle' : 'rolling';
                        return (
                            <Die
                                key={`${phaseKey}-${i}`}
                                faces={FATE_DIE_FACES}
                                faceLabels={FATE_FACE_LABELS}
                                result={dieResult}
                                animating={phase !== 'idle'}
                                reduced={reduced}
                                onSettled={
                                    phase === 'tumbling' && isLast
                                        ? handleDieSettled : null
                                }
                            />
                        );
                    })}
                </div>
                {isRoller && phase === 'idle' && !isAuto && (
                    <div className={'fate-die-overlay__buttons'}>
                        <button
                            type="button"
                            className={'fate-die-overlay__btn '
                                + 'fate-die-overlay__btn--wait'}
                            onClick={handleWaitClick}
                            data-testid="fate-die-overlay-wait"
                        >Wait</button>
                        <button
                            type="button"
                            className={'fate-die-overlay__btn '
                                + 'fate-die-overlay__btn--roll'}
                            onClick={handleRollerClick}
                            data-testid="fate-die-overlay-roll"
                        >Roll ({countdown}s)</button>
                    </div>
                )}
            </div>
        </div>
    );

    if (typeof document === 'undefined' || !document.body) return null;
    return createPortal(overlay, document.body);
}
